import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import { Confirm, useCanAccess, useDataProvider, useNotify } from 'react-admin';

import { DialogCloseButton } from '@/components/misc/DialogCloseButton';
import { FileDropzone } from '@/components/misc/FileDropzone';
import { QuoteAttachmentPreviewDialog } from '@/components/QuoteAttachmentPreviewDialog';
import { QuoteAttachment } from '@/utils/quote_attachment_utils';
import type { CrmDataProvider } from '@/providers/types';

import type {
    PermitDocumentItem,
    PermitFileSyncResult,
    PermitPackage,
    PermitSection,
} from './projectPermitsTypes';

type NewPermitFileState = {
    name: string;
    file_name: string;
    file: File | null;
    uploaded_path: string;
    audience: 'owner' | 'internal';
    sort_order: string;
    section_id: number | null;
};

const emptyNewPermitFile = (): NewPermitFileState => ({
    name: '',
    file_name: '',
    file: null,
    uploaded_path: '',
    audience: 'internal',
    sort_order: '0',
    section_id: null,
});

type Props = {
    open: boolean;
    onClose: () => void;
    projectId?: number | string;
    // Municipality mode: manage a municipality's permit templates with no project.
    // Project-only actions (generate, generate-in-project-dir) are hidden; preview requires projectId.
    municipalityId?: number;
    initialPackage?: PermitPackage | null;
    onPackageChange?: (pkg: PermitPackage) => void;
    onFilesGenerated?: () => void;
};

export const ProjectPermitsDialog = ({
    open,
    onClose,
    projectId,
    municipalityId,
    initialPackage = null,
    onPackageChange,
    onFilesGenerated,
}: Props) => {
    const isMunicipalityMode = municipalityId != null;
    const notify = useNotify();
    const dataProvider = useDataProvider<CrmDataProvider>();
    const { canAccess: canEditPermits = false } = useCanAccess({
        resource: 'permit',
        action: 'update',
    });
    const { canAccess: canDeletePermits = false } = useCanAccess({
        resource: 'permit',
        action: 'delete',
    });

    const [loading, setLoading] = React.useState(!initialPackage);
    const [pkg, setPkg] = React.useState<PermitPackage | null>(initialPackage);
    const [error, setError] = React.useState<string | null>(null);
    const [selectedMunicipalityKey, setSelectedMunicipalityKey] = React.useState(
        initialPackage?.recommended_municipality_key ?? ''
    );
    const [savingMunicipality, setSavingMunicipality] = React.useState(false);
    const [driveKey, setDriveKey] = React.useState<string | null>(null);
    const [downloadAllLoading, setDownloadAllLoading] = React.useState(false);
    const [syncing, setSyncing] = React.useState(false);
    const [syncResult, setSyncResult] = React.useState<PermitFileSyncResult | null>(null);
    const [syncError, setSyncError] = React.useState<string | null>(null);

    const [previewAttachment, setPreviewAttachment] =
        React.useState<QuoteAttachment | null>(null);
    const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
    const [previewLoading, setPreviewLoading] = React.useState(false);

    const [addOpen, setAddOpen] = React.useState(false);
    const [addForm, setAddForm] = React.useState<NewPermitFileState>(emptyNewPermitFile());
    const [addSaving, setAddSaving] = React.useState(false);
    const [editingFileId, setEditingFileId] = React.useState<number | null>(null);
    const [editForm, setEditForm] = React.useState<NewPermitFileState>(emptyNewPermitFile());
    const editOrigSectionId = React.useRef<number | null>(null);
    const [editSaving, setEditSaving] = React.useState(false);
    const [deletingFileId, setDeletingFileId] = React.useState<number | null>(null);
    const [confirmDeleteDoc, setConfirmDeleteDoc] =
        React.useState<PermitDocumentItem | null>(null);
    const [sectionAddOpen, setSectionAddOpen] = React.useState(false);
    const [sectionName, setSectionName] = React.useState('');
    const [sectionSaving, setSectionSaving] = React.useState(false);
    const [deletingSectionId, setDeletingSectionId] = React.useState<number | null>(null);

    const applyPackage = React.useCallback(
        (next: PermitPackage) => {
            setPkg(next);
            if (next.recommended_municipality_key) {
                setSelectedMunicipalityKey(next.recommended_municipality_key);
            }
            onPackageChange?.(next);
        },
        [onPackageChange]
    );

    const refetchPackage = React.useCallback(async () => {
        const data =
            municipalityId != null
                ? await dataProvider.getMunicipalityPermits(municipalityId)
                : await dataProvider.getProjectPermits(projectId!);
        applyPackage(data);
        return data;
    }, [projectId, municipalityId, applyPackage, dataProvider]);

    React.useEffect(() => {
        if (!open) return;
        setSyncResult(null);
        setSyncError(null);
        if (initialPackage) {
            setPkg(initialPackage);
            setSelectedMunicipalityKey(initialPackage.recommended_municipality_key ?? '');
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        refetchPackage()
            .catch(err => {
                if (!cancelled) setError(err?.message || 'Could not load permit documents.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    React.useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    const saveMunicipalitySelection = async () => {
        if (!selectedMunicipalityKey) return;
        setSavingMunicipality(true);
        try {
            await dataProvider.setProjectPermitMunicipality(
                projectId!,
                selectedMunicipalityKey,
            );
            // The save response has no sections; refetch so the include is sent.
            await refetchPackage();
            notify('Permit municipality saved', { type: 'success' });
        } catch (err: any) {
            notify(err?.message || 'Could not save municipality selection.', {
                type: 'error',
            });
        } finally {
            setSavingMunicipality(false);
        }
    };

    const previewDocument = async (doc: PermitDocumentItem) => {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
        const filename = doc.template_filename || '';
        setPreviewAttachment({
            id: `permit-${doc.key}`,
            name: doc.title,
            filename,
            content_type: 'application/pdf',
        });
        setPreviewLoading(true);
        try {
            const { blob, contentType: headerType } = projectId
                ? await dataProvider.previewProjectPermit(projectId, doc.key)
                : await dataProvider.previewPermitFile(doc.permit_file_id!);
            const ext = filename.toLowerCase().split('.').pop() || '';
            const extMime: Record<string, string> = {
                pdf: 'application/pdf',
                png: 'image/png',
                jpg: 'image/jpeg',
                jpeg: 'image/jpeg',
            };
            const contentType =
                headerType && headerType !== 'application/octet-stream'
                    ? headerType
                    : blob.type || extMime[ext] || 'application/pdf';
            const url = URL.createObjectURL(blob);
            setPreviewAttachment({
                id: `permit-${doc.key}`,
                name: doc.title,
                filename,
                content_type: contentType,
            });
            setPreviewUrl(url);
        } catch (err: any) {
            notify(err?.message || 'Could not load preview.', { type: 'error' });
            setPreviewAttachment(null);
        } finally {
            setPreviewLoading(false);
        }
    };

    const closePreview = () => {
        setPreviewAttachment(null);
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
    };

    const downloadToDrive = async (docKey: string) => {
        setDriveKey(docKey);
        try {
            await dataProvider.downloadProjectPermitToDrive(projectId!, docKey);
            notify('Saved to Drive', { type: 'success' });
            onFilesGenerated?.();
        } catch (err: any) {
            notify(err?.message || 'Could not save to Drive.', { type: 'error' });
        } finally {
            setDriveKey(null);
        }
    };

    const downloadAllToDrive = async () => {
        setDownloadAllLoading(true);
        try {
            const body =
                await dataProvider.downloadAllProjectPermitsToDrive(projectId!);
            const count = Array.isArray(body?.documents) ? body.documents.length : 4;
            notify(`Saved ${count} documents to Drive`, { type: 'success' });
            onFilesGenerated?.();
        } catch (err: any) {
            notify(err?.message || 'Could not save all documents to Drive.', { type: 'error' });
        } finally {
            setDownloadAllLoading(false);
        }
    };

    const syncMunicipality = async () => {
        const municipalityId = pkg?.municipality_id;
        if (!municipalityId || syncing) return;
        setSyncing(true);
        setSyncResult(null);
        setSyncError(null);
        try {
            setSyncResult(await dataProvider.syncMunicipalityPermitFiles(municipalityId));
            // Refetch so newly created or linked documents appear in the lists.
            await refetchPackage();
        } catch (err: any) {
            setSyncError(err?.message || 'Could not sync permit documents.');
        } finally {
            setSyncing(false);
        }
    };

    const openAdd = () => {
        setAddForm(emptyNewPermitFile());
        setAddOpen(true);
    };

    const submitAdd = async () => {
        if (!pkg?.municipality_id) {
            notify('Select a municipality before adding a permit file.', { type: 'warning' });
            return;
        }
        setAddSaving(true);
        try {
            const key = addForm.file_name
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '_')
                .replace(/^_+|_+$/g, '');
            const created = await dataProvider.create('permit-files', {
                data: {
                    municipality_id: pkg.municipality_id,
                    key,
                    name: addForm.name,
                    file_name: addForm.file_name,
                    audience: addForm.audience,
                    sort_order: Number(addForm.sort_order) || 0,
                },
            });
            const newId = Number((created as any)?.data?.id);
            if (addForm.file && Number.isFinite(newId)) {
                await dataProvider.uploadPermitFile(newId, addForm.file);
            }
            notify('Permit file added', { type: 'success' });
            setAddOpen(false);
            await refetchPackage();
        } catch (err: any) {
            notify(err?.message || 'Could not create permit file.', { type: 'error' });
        } finally {
            setAddSaving(false);
        }
    };

    const openEdit = (doc: PermitDocumentItem) => {
        if (!doc.permit_file_id) return;
        const sectionId = hasSection(doc) ? (doc.section_id ?? null) : null;
        editOrigSectionId.current = sectionId;
        setEditingFileId(doc.permit_file_id);
        setEditForm({
            name: doc.title,
            file_name: doc.template_filename,
            file: null,
            uploaded_path: doc.storage_file_path ?? '',
            audience: (doc.audience as 'owner' | 'internal') || 'owner',
            sort_order: String(doc.sort_order ?? 0),
            section_id: sectionId,
        });
    };

    const submitEdit = async () => {
        if (editingFileId == null) return;
        setEditSaving(true);
        try {
            await dataProvider.update('permit-files', {
                id: editingFileId,
                data: {
                    name: editForm.name,
                    file_name: editForm.file_name,
                    audience: editForm.audience,
                    sort_order: Number(editForm.sort_order) || 0,
                    // only send section_id when the user actually changed it, so an
                    // unrelated edit doesn't flip section_set_by_hand and freeze sync
                    ...(editForm.section_id !== editOrigSectionId.current
                        ? { section_id: editForm.section_id }
                        : {}),
                },
                previousData: { id: editingFileId },
            });
            if (editForm.file) {
                await dataProvider.uploadPermitFile(editingFileId, editForm.file);
            }
            notify('Permit file updated', { type: 'success' });
            setEditingFileId(null);
            await refetchPackage();
        } catch (err: any) {
            notify(err?.message || 'Could not update permit file.', { type: 'error' });
        } finally {
            setEditSaving(false);
        }
    };

    const deleteFile = async (doc: PermitDocumentItem) => {
        if (!doc.permit_file_id) return;
        setDeletingFileId(doc.permit_file_id);
        try {
            await dataProvider.delete('permit-files', {
                id: doc.permit_file_id,
                previousData: { id: doc.permit_file_id },
            });
            notify('Permit file deleted', { type: 'success' });
            await refetchPackage();
        } catch (err: any) {
            // 404 = row already removed (stale list); reconcile instead of erroring.
            if (err?.status === 404) {
                notify('Permit file already removed', { type: 'info' });
                await refetchPackage();
            } else {
                notify(err?.message || 'Could not delete permit file.', { type: 'error' });
            }
        } finally {
            setDeletingFileId(null);
        }
    };

    const sections: PermitSection[] = pkg?.sections ?? [];
    const sectionIds = new Set(sections.map(s => s.id));
    const hasSection = (doc: PermitDocumentItem) =>
        doc.section_id != null && sectionIds.has(doc.section_id);

    const openSectionAdd = () => {
        setSectionName('');
        setSectionAddOpen(true);
    };

    const submitSection = async () => {
        const name = sectionName.trim();
        if (!pkg?.municipality_id || !name) return;
        setSectionSaving(true);
        try {
            await dataProvider.create('permit-sections', {
                data: { municipality_id: pkg.municipality_id, name },
            });
            notify('Section created', { type: 'success' });
            setSectionAddOpen(false);
            await refetchPackage();
        } catch (err: any) {
            notify(err?.message || 'Could not create section.', { type: 'error' });
        } finally {
            setSectionSaving(false);
        }
    };

    const deleteSection = async (section: PermitSection) => {
        setDeletingSectionId(section.id);
        try {
            await dataProvider.delete('permit-sections', {
                id: section.id,
                previousData: { id: section.id },
            });
            notify('Section deleted', { type: 'success' });
            await refetchPackage();
        } catch (err: any) {
            notify(err?.message || 'Could not delete section.', { type: 'error' });
        } finally {
            setDeletingSectionId(null);
        }
    };

    const renderDocItem = (doc: PermitDocumentItem) => (
        <ListItem
            key={doc.key}
            sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, py: 1.5 }}
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                gap={1}
                sx={{ width: '100%' }}
            >
                <ListItemText
                    primary={doc.title}
                    secondary={
                        <>
                            {doc.template_filename}
                            {doc.audience ? (
                                <Typography variant="caption" component="div" color="text.secondary">
                                    {doc.audience.charAt(0).toUpperCase() + doc.audience.slice(1)}
                                </Typography>
                            ) : null}
                        </>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                    sx={{ minWidth: 0, pr: 2 }}
                />
                <Stack direction="row" gap={1} flexWrap="wrap">
                    {projectId || doc.permit_file_id ? (
                        <Button
                            size="small"
                            onClick={() => previewDocument(doc)}
                            disabled={previewLoading}
                        >
                            Preview
                        </Button>
                    ) : null}
                    {!isMunicipalityMode ? (
                        <Button
                            size="small"
                            variant="contained"
                            disabled={driveKey === doc.key}
                            onClick={() => downloadToDrive(doc.key)}
                        >
                            {driveKey === doc.key ? 'Generating…' : 'Generate'}
                        </Button>
                    ) : null}
                    {canEditPermits && doc.permit_file_id ? (
                        <IconButton
                            size="small"
                            aria-label="Edit permit file"
                            onClick={() => openEdit(doc)}
                        >
                            <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                    ) : null}
                    {canDeletePermits && doc.permit_file_id ? (
                        <IconButton
                            size="small"
                            aria-label="Delete permit file"
                            disabled={deletingFileId === doc.permit_file_id}
                            onClick={() => setConfirmDeleteDoc(doc)}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    ) : null}
                </Stack>
            </Stack>
        </ListItem>
    );

    const renderPermitForm = (
        form: NewPermitFileState,
        setForm: React.Dispatch<React.SetStateAction<NewPermitFileState>>,
        showSection = false
    ) => (
        <Stack gap={2} sx={{ pt: 1 }}>
            <TextField
                label="Name"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                fullWidth
                size="small"
            />
            <TextField
                label="File name"
                value={form.file_name}
                onChange={e => setForm(f => ({ ...f, file_name: e.target.value }))}
                fullWidth
                size="small"
            />
            <Stack gap={0.5}>
                <FileDropzone
                    maxFiles={1}
                    label="Drop a file here, or click to choose"
                    value={form.file ? [form.file] : []}
                    onChange={files => setForm(f => ({ ...f, file: files[0] ?? null }))}
                />
                {form.file ? null : form.uploaded_path ? (
                    <Typography variant="caption" color="text.secondary">
                        Current: {form.uploaded_path}
                    </Typography>
                ) : (
                    <Typography variant="caption" color="text.secondary">
                        No file uploaded yet — template will be resolved from the
                        municipality folder.
                    </Typography>
                )}
            </Stack>
            <FormControl size="small" fullWidth>
                <InputLabel id="permit-file-audience">Audience</InputLabel>
                <Select
                    labelId="permit-file-audience"
                    label="Audience"
                    value={form.audience}
                    onChange={e =>
                        setForm(f => ({
                            ...f,
                            audience: e.target.value as 'owner' | 'internal',
                        }))
                    }
                >
                    <MenuItem value="owner">Owner</MenuItem>
                    <MenuItem value="internal">Internal</MenuItem>
                </Select>
            </FormControl>
            {showSection && sections.length > 0 ? (
                <FormControl size="small" fullWidth>
                    <InputLabel id="permit-file-section">Section</InputLabel>
                    <Select
                        labelId="permit-file-section"
                        label="Section"
                        value={form.section_id == null ? '' : String(form.section_id)}
                        onChange={e =>
                            setForm(f => ({
                                ...f,
                                section_id:
                                    e.target.value === '' ? null : Number(e.target.value),
                            }))
                        }
                    >
                        <MenuItem value="">City Documents</MenuItem>
                        {sections.map(section => (
                            <MenuItem key={section.id} value={String(section.id)}>
                                {section.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            ) : null}
            <TextField
                label="Sort order"
                value={form.sort_order}
                onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))}
                type="number"
                size="small"
                fullWidth
            />
        </Stack>
    );

    const renderBody = () => {
        if (loading) {
            return (
                <Stack alignItems="center" py={4}>
                    <CircularProgress size={28} />
                </Stack>
            );
        }
        if (error) {
            return <Typography color="text.secondary">{error}</Typography>;
        }
        if (!pkg) return null;

        return (
            <Stack gap={2}>
                {pkg.needs_municipality_selection ? (
                    <Card sx={{ p: 2 }}>
                        <Stack gap={2}>
                            {pkg.recommendation_reason ? (
                                <Typography variant="body2" color="text.secondary">
                                    {pkg.recommendation_reason}
                                    {pkg.recommended_municipality_key
                                        ? ' — a suggested municipality is pre-selected below.'
                                        : ''}
                                </Typography>
                            ) : null}
                            <FormControl fullWidth size="small">
                                <InputLabel id="permit-municipality-label">
                                    Municipality
                                </InputLabel>
                                <Select
                                    labelId="permit-municipality-label"
                                    label="Municipality"
                                    value={selectedMunicipalityKey}
                                    onChange={e =>
                                        setSelectedMunicipalityKey(String(e.target.value))
                                    }
                                >
                                    {(pkg.available_municipalities ?? []).map(option => (
                                        <MenuItem key={option.key} value={option.key}>
                                            {option.label}
                                            {option.key === pkg.recommended_municipality_key
                                                ? ' (recommended)'
                                                : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Button
                                variant="contained"
                                disabled={!selectedMunicipalityKey || savingMunicipality}
                                onClick={saveMunicipalitySelection}
                            >
                                {savingMunicipality ? 'Saving…' : 'Use this municipality'}
                            </Button>
                        </Stack>
                    </Card>
                ) : null}

                {canEditPermits && pkg.municipality_id ? (
                    <Stack gap={1} sx={{ px: 1 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            sx={{ alignSelf: 'flex-start' }}
                            disabled={syncing}
                            startIcon={syncing ? <CircularProgress size={14} /> : <SyncIcon />}
                            onClick={syncMunicipality}
                        >
                            {syncing ? 'Syncing…' : 'Sync this municipality'}
                        </Button>
                        {syncResult ? (
                            <Typography variant="body2" color="text.secondary" role="status">
                                {`Created ${syncResult.created} · Linked ${syncResult.linked + syncResult.restored}${syncResult.restored ? ` (includes ${syncResult.restored} restored)` : ''} · Soft-deleted ${syncResult.soft_deleted}`}
                            </Typography>
                        ) : null}
                        {syncError ? <Alert severity="error">{syncError}</Alert> : null}
                    </Stack>
                ) : null}

                {!pkg.documents_configured ? null : (
                    <>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                            justifyContent="space-between"
                            gap={1}
                            sx={{ px: 1 }}
                        >
                            <Typography variant="subtitle1">City Documents</Typography>
                            <Stack direction="row" gap={1} flexWrap="wrap">
                                {canEditPermits ? (
                                    <Button
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={openAdd}
                                    >
                                        Add permit file
                                    </Button>
                                ) : null}
                                {canEditPermits && pkg.municipality_id ? (
                                    <Button
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={openSectionAdd}
                                    >
                                        Add section
                                    </Button>
                                ) : null}
                                {!isMunicipalityMode ? (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        disabled={downloadAllLoading || !!driveKey}
                                        onClick={downloadAllToDrive}
                                    >
                                        {downloadAllLoading
                                            ? 'Generating…'
                                            : 'Generate in Project Dir'}
                                    </Button>
                                ) : null}
                            </Stack>
                        </Stack>

                        <Card>
                            <List dense>
                                {[...pkg.owner_documents, ...pkg.internal_documents]
                                    .filter(d => !hasSection(d))
                                    .map(renderDocItem)}
                            </List>
                        </Card>

                        {sections.map(section => {
                            const sectionDocs = [
                                ...pkg.owner_documents,
                                ...pkg.internal_documents,
                            ].filter(d => d.section_id === section.id);
                            return (
                                <React.Fragment key={section.id}>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        sx={{ px: 1 }}
                                    >
                                        <Typography variant="subtitle1">{section.name}</Typography>
                                        {canDeletePermits ? (
                                            <IconButton
                                                size="small"
                                                aria-label="Delete section"
                                                disabled={deletingSectionId === section.id}
                                                onClick={() => deleteSection(section)}
                                            >
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        ) : null}
                                    </Stack>
                                    <Card>
                                        {sectionDocs.length > 0 ? (
                                            <List dense>{sectionDocs.map(renderDocItem)}</List>
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ p: 2 }}
                                            >
                                                No documents in this section.
                                            </Typography>
                                        )}
                                    </Card>
                                </React.Fragment>
                            );
                        })}
                    </>
                )}
            </Stack>
        );
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ pr: 6 }}>
                    Permit documents
                    <DialogCloseButton onClose={onClose} />
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ pt: 1 }}>{renderBody()}</Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>

            <QuoteAttachmentPreviewDialog
                attachment={previewAttachment}
                sourceUrl={previewUrl}
                loading={previewLoading}
                onClose={closePreview}
            />

            <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ pr: 6 }}>
                    Add permit file
                    <DialogCloseButton onClose={() => setAddOpen(false)} />
                </DialogTitle>
                <DialogContent dividers>{renderPermitForm(addForm, setAddForm)}</DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={submitAdd}
                        disabled={addSaving || !addForm.name || !addForm.file_name}
                    >
                        {addSaving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={editingFileId != null}
                onClose={() => setEditingFileId(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ pr: 6 }}>
                    Edit permit file
                    <DialogCloseButton onClose={() => setEditingFileId(null)} />
                </DialogTitle>
                <DialogContent dividers>
                    {renderPermitForm(editForm, setEditForm, true)}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditingFileId(null)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={submitEdit}
                        disabled={editSaving || !editForm.name || !editForm.file_name}
                    >
                        {editSaving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={sectionAddOpen}
                onClose={() => setSectionAddOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ pr: 6 }}>
                    Add section
                    <DialogCloseButton onClose={() => setSectionAddOpen(false)} />
                </DialogTitle>
                <DialogContent dividers>
                    <TextField
                        label="Section name"
                        value={sectionName}
                        onChange={e => setSectionName(e.target.value)}
                        fullWidth
                        size="small"
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSectionAddOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={submitSection}
                        disabled={sectionSaving || !sectionName.trim()}
                    >
                        {sectionSaving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Confirm
                isOpen={confirmDeleteDoc != null}
                title="Delete permit document"
                content={`Delete "${confirmDeleteDoc?.title ?? ''}"? This also removes the template file from SharePoint and cannot be undone.`}
                confirm="Delete"
                onConfirm={() => {
                    const doc = confirmDeleteDoc;
                    setConfirmDeleteDoc(null);
                    if (doc) void deleteFile(doc);
                }}
                onClose={() => setConfirmDeleteDoc(null)}
            />
        </>
    );
};
